import React, {useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Col, Form, FormLabel} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {useForm, useFormState} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {SYSTEM_UNION_CODE} from '../../config';
import CustomFormSwitch from '../forms/CustomFormSwitch';
import CustomFormSelect from '../forms/CustomFormSelect';
import CustomFormInput from '../forms/CustomFormInput';
import CustomFormTextArea from '../forms/CustomFormTextArea';

const AVAILABLE_ROLES = ['USER', 'ADMIN', 'UNION_ADMIN'];

const blankToNull = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }
  return value;
};

const IDPSettingsForm = ({defaultValues, onSubmit, onError}) => {
  const {t} = useTranslation();
  const unionModeEnabled = Boolean(SYSTEM_UNION_CODE);

  const validationSchema = useMemo(() => {
    const roleMappingShape = AVAILABLE_ROLES.reduce((shape, role) => {
      shape[role] = yup.string().nullable().transform(blankToNull).max(1000);
      return shape;
    }, {});

    return yup.object({
      countryCode: yup.string().nullable(),
      enabled: yup.boolean().default(false),
      idpType: yup
        .string()
        .nullable()
        .transform(blankToNull),
      // .when('enabled', (enabled, schema) =>
      //   enabled
      //     ? schema.required(t('idpSettings:msgTypeRequired'))
      //     : schema
      // ),
      oauth2IssuerUri: yup
        .string()
        .nullable()
        .transform(blankToNull)
        .max(1000)
        .when(['enabled', 'idpType'], ([enabled, idpType], schema) =>
          unionModeEnabled && enabled && (idpType === 'OIDC_SECRET' || idpType === 'OIDC_PKCE')
            ? schema.required(t('validation:msgRequired'))
            : schema
        ),
      oauth2ClientId: yup
        .string()
        .nullable()
        .transform(blankToNull)
        .max(255)
        .when(['enabled', 'idpType'], ([enabled, idpType], schema) =>
          unionModeEnabled && enabled && (idpType === 'OIDC_SECRET' || idpType === 'OIDC_PKCE')
            ? schema.required(t('validation:msgRequired'))
            : schema
        ),
      oauth2ClientSecret: yup
        .string()
        .nullable()
        .transform(blankToNull)
        .max(4000)
        .when(['enabled', 'idpType'], ([enabled, idpType], schema) =>
          unionModeEnabled && enabled && (idpType === 'OIDC_SECRET')
            ? schema.required(t('validation:msgRequired'))
            : schema
        ),
      oauth2ScopesCsv: yup
        .string()
        .nullable()
        .transform(blankToNull)
        .max(2000),
      samlMetadataUrl: yup
        .string()
        .nullable()
        .transform(blankToNull)
        .max(1000)
        .test(
          'saml-metadata-url-or-xml',
          t('idpSettings:msgSamlMetadataRequired'),
          function (value) {
            if (!unionModeEnabled) {
              return true;
            }
            const {enabled, idpType, samlMetadataXml} = this.parent;
            if (!enabled || idpType !== 'SAML') {
              return true;
            }
            return Boolean(blankToNull(value) || blankToNull(samlMetadataXml));
          }
        ),
      samlMetadataXml: yup
        .string()
        .nullable()
        .transform(blankToNull)
        .test(
          'saml-metadata-xml-or-url',
          t('idpSettings:msgSamlMetadataRequired'),
          function (value) {
            if (!unionModeEnabled) {
              return true;
            }
            const {enabled, idpType, samlMetadataUrl} = this.parent;
            if (!enabled || idpType !== 'SAML') {
              return true;
            }
            return Boolean(blankToNull(value) || blankToNull(samlMetadataUrl));
          }
        ),
      samlEntityId: yup.string().nullable().transform(blankToNull).max(1000),
      samlNameIdFormat: yup.string().nullable().transform(blankToNull).max(500),
      samlWantAssertionsSigned: yup.boolean().default(true),
      //samlSignAuthnRequests: yup.boolean().default(false),
      roleMappings: yup.object(roleMappingShape),
    });
  }, [t, unionModeEnabled]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: {errors},
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const {isValid, dirtyFields, isDirty, isSubmitting} = useFormState({
    control,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const idpType = watch('idpType');
  const idpTypeOptions = useMemo(
    () => [
      {id: 'OIDC_PKCE', valueKey: 'idpSettings:optOidc'},
      {id: 'OIDC_SECRET', valueKey: 'idpSettings:optOAuth2'},
      {id: 'SAML', valueKey: 'idpSettings:optSaml'},
    ],
    []
  );
  const showOAuth2 = idpType === 'OIDC_SECRET' || idpType === 'OIDC_PKCE';
  const showSaml = idpType === 'SAML';

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      {unionModeEnabled && (
        <>
          <div className="row mb-4">
            <div className="col-xs-12 col-md-6 mb-3">
              <FormLabel className="fw-bold" htmlFor="enabled">
                {t('idpSettings:lblSSOEnabled')}
              </FormLabel>
              <CustomFormSwitch
                fieldName="enabled"
                register={register}
                dirtyFields={dirtyFields}
                errorMessage={errors?.enabled?.message}
              />
            </div>
            <div className="col-xs-12 col-md-6 mb-3">
              <FormLabel className="fw-bold" htmlFor="idpType">
                {t('idpSettings:lblIdpType')}
              </FormLabel>
              <CustomFormSelect
                fieldName="idpType"
                register={register}
                dirtyFields={dirtyFields}
                options={idpTypeOptions}
                errorMessage={errors?.idpType?.message}
                skipNull={true}
              />
            </div>
          </div>

          {showOAuth2 && (
            <fieldset className="border rounded p-3 mb-4">
              <legend className="float-none w-auto px-2 fs-6">
                {t('idpSettings:hdgOAuth2')}
              </legend>
              <div className="row">
                <div className="col-xs-12  mb-3">
                  <FormLabel className="fw-bold" htmlFor="oauth2IssuerUri">
                    {t('idpSettings:lblOAuth2IssuerUri')}<span className="requiredIndicator">*</span>
                  </FormLabel>
                  <CustomFormInput
                    fieldName="oauth2IssuerUri"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.oauth2IssuerUri?.message}
                  />
                </div>
                <div className="col-xs-12  mb-3">
                  <FormLabel className="fw-bold" htmlFor="oauth2ClientId">
                    {t('idpSettings:lblOAuth2ClientId')}<span
                    className="requiredIndicator">*</span>
                  </FormLabel>
                  <CustomFormInput
                    fieldName="oauth2ClientId"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.oauth2ClientId?.message}
                  />
                </div>
                <div className="col-xs-12  mb-3">
                  <FormLabel className="fw-bold" htmlFor="oauth2ClientSecret">
                    {t('idpSettings:lblOAuth2ClientSecret')}
                    {idpType === "OIDC_SECRET" && <span className="requiredIndicator">*</span>}
                  </FormLabel>
                  <CustomFormInput
                    fieldName="oauth2ClientSecret"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.oauth2ClientSecret?.message}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 mb-3">
                  <FormLabel className="fw-bold" htmlFor="oauth2ScopesCsv">
                    {t('idpSettings:lblOAuth2ScopesCsv')}
                  </FormLabel>
                  <CustomFormInput
                    fieldName="oauth2ScopesCsv"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.oauth2ScopesCsv?.message}
                  />
                </div>
              </div>
            </fieldset>
          )}

          {showSaml && (
            <fieldset className="border rounded p-3 mb-4">
              <legend className="float-none w-auto px-2 fs-6">
                {t('idpSettings:hdgSaml')}
              </legend>
              <div className="row">
                <div className="col-xs-12 mb-3">
                  <FormLabel className="fw-bold" htmlFor="samlMetadataUrl">
                    {t('idpSettings:lblSamlMetadataUrl')}
                  </FormLabel>
                  <CustomFormInput
                    fieldName="samlMetadataUrl"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.samlMetadataUrl?.message}
                  />
                  <Form.Text className="text-muted">
                    {t('idpSettings:txtSamlMetadataHelper')}
                  </Form.Text>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 mb-3">
                  <FormLabel className="fw-bold" htmlFor="samlMetadataXml">
                    {t('idpSettings:lblSamlMetadataXml')}
                  </FormLabel>
                  <CustomFormTextArea
                    fieldName="samlMetadataXml"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.samlMetadataXml?.message}
                    params={{rows: 6}}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-md-6 mb-3">
                  <FormLabel className="fw-bold" htmlFor="samlEntityId">
                    {t('idpSettings:lblSamlEntityId')}
                  </FormLabel>
                  <CustomFormInput
                    fieldName="samlEntityId"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.samlEntityId?.message}
                  />
                </div>
                <div className="col-xs-12 col-md-6 mb-3">
                  <FormLabel className="fw-bold" htmlFor="samlNameIdFormat">
                    {t('idpSettings:lblSamlNameIdFormat')}
                  </FormLabel>
                  <CustomFormInput
                    fieldName="samlNameIdFormat"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.samlNameIdFormat?.message}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-md-6 mb-3">
                  <FormLabel className="fw-bold" htmlFor="samlWantAssertionsSigned">
                    {t('idpSettings:lblSamlWantAssertionsSigned')}
                  </FormLabel>
                  <CustomFormSwitch
                    fieldName="samlWantAssertionsSigned"
                    register={register}
                    dirtyFields={dirtyFields}
                    errorMessage={errors?.samlWantAssertionsSigned?.message}
                  />
                </div>
                <div className="col-xs-12 col-md-6 mb-3">
                  {/*<FormLabel className="fw-bold" htmlFor="samlSignAuthnRequests">*/}
                  {/*  {t('idpSettings:lblSamlSignAuthnRequests')}*/}
                  {/*</FormLabel>*/}
                  {/*<CustomFormSwitch*/}
                  {/*  fieldName="samlSignAuthnRequests"*/}
                  {/*  register={register}*/}
                  {/*  dirtyFields={dirtyFields}*/}
                  {/*  errorMessage={errors?.samlSignAuthnRequests?.message}*/}
                  {/*/>*/}
                </div>
              </div>
            </fieldset>
          )}
        </>
      )}

      <fieldset className="border rounded p-3 mb-4">
        <legend className="float-none w-auto px-2 fs-6">
          {t('idpSettings:hdgRoleMappings', 'Role mappings')}
        </legend>
        <Form.Text className="d-block mb-3" id="roleMappingsDescription">
          {t(
            'idpSettings:txtRoleMappingsDescription',
            'Enter the roles provided by your identity provider that should map to each of the application roles. Use commas to separate multiple values. Use * to indicate any roles.'
          )}
        </Form.Text>
        {AVAILABLE_ROLES.map((role) => (
          <div className="row align-items-center mb-3" key={role}>
            <div className="col-xs-12 col-md-4">
              <FormLabel className="fw-bold" htmlFor={`roleMappings.${role}`}>
                {t('idpSettings:lblRoleMappingRole', {defaultValue: role, role})}
              </FormLabel>
            </div>
            <div className="col-xs-12 col-md-8">
              <CustomFormInput
                fieldName={`roleMappings.${role}`}
                register={register}
                dirtyFields={dirtyFields}
                errorMessage={errors?.roleMappings?.[role]?.message}
              />
              <Form.Text className="text-muted" id={`roleMappingsHelp-${role}`}>
                {t('idpSettings:txtRoleMappingFieldHelp', {
                  defaultValue: 'Map to {{role}} role',
                  role,
                })}
              </Form.Text>
            </div>
          </div>
        ))}
      </fieldset>

      <Form.Group className="row">
        <Col>
          <button
            type="submit"
            className="btn btn-success mt-3 me-2"
            disabled={!isValid || isSubmitting || !isDirty}
          >
            {t('common:btnSaveChanges')}
          </button>
        </Col>
      </Form.Group>
    </form>
  );
};

IDPSettingsForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default IDPSettingsForm;
